/**
 * LineagePage (route: /prioritization/lineage)
 *
 * System-wide lineage feed. Answers "where did items come from and how were
 * they transformed?" — NOT "what happened to items over time?"
 *
 * Canon distinction (01-terminology-and-glossary_v3.md):
 *
 *   Lineage  = source, identity-shaping transformations, origin artifacts
 *              → lineage_events rows (split/merge/supersede/retire/fork)
 *              → item_created and item_promoted from audit_events
 *
 *   History  = observational audit trail over time
 *              → field edits, value events, pool membership, governance
 *                transitions, session result appearances, overrides
 *              → NOT shown here; shown in item History tab and system audit log
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  GitBranch,
  Search,
  X,
  RefreshCw,
  GitMerge,
  ArrowUpCircle,
  Archive,
  Copy,
  Filter,
  Plus,
  ExternalLink,
  User,
  Calendar,
  ChevronDown,
  ChevronUp,
  Map as MapIcon,
} from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useActorDisplayMap } from '../../hooks/prioritization/useActorDisplayMap';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
} from '../../lib/supabaseSync';
import type {
  ProvenanceEvent,
  ProvenanceEventKind,
  ProvenanceObjectRef,
  ProvenanceObjectKind,
} from '../../types/prioritization/decisions';
import {
  PROVENANCE_KIND_LABELS,
  PROVENANCE_KIND_COLORS,
  PROVENANCE_OBJECT_KIND_LABELS,
} from '../../types/prioritization/decisions';

// ============================================================================
// Lineage event kinds shown on this page (canon-aligned subset)
// ============================================================================

/**
 * Audit event types that represent item origin / identity-shaping events.
 *
 * Note: item creation is logged as event_type='shaping_started' with
 * payload.details.action='item_created' — it is NOT stored as 'item_created'.
 * We include 'shaping_started' here and filter client-side by payload.
 */
const LINEAGE_AUDIT_EVENT_TYPES = new Set([
  'shaping_started',  // item creation — must check payload.details.action
  'item_created',     // explicit creation events (newer paths)
  'item_promoted',
  'ITEM_PROMOTED',
]);

/** The ProvenanceEventKinds surfaced on this page. */
const LINEAGE_KINDS: ProvenanceEventKind[] = ['lineage', 'item_transition', 'other'];

// ============================================================================
// Icons
// ============================================================================

const KIND_ICONS: Partial<Record<ProvenanceEventKind, React.ElementType>> = {
  lineage:        GitBranch,
  item_transition: ArrowUpCircle,
  other:          GitBranch,
};

const LINEAGE_EVENT_ICONS: Record<string, React.ElementType> = {
  split:     GitBranch,
  merge:     GitMerge,
  supersede: ArrowUpCircle,
  retire:    Archive,
  fork:      Copy,
  item_created:  Plus,
  item_promoted: ArrowUpCircle,
  ITEM_PROMOTED: ArrowUpCircle,
};

// ============================================================================
// Object href builder
// ============================================================================

function objectHref(kind: ProvenanceObjectKind, id: string): string {
  switch (kind) {
    case 'item':    return `/prioritization/items/${id}`;
    case 'session': return `/prioritization/sessions/${id}`;
    case 'cycle':   return `/prioritization/cycles/${id}`;
    case 'pool':    return `/prioritization/lists/${id}`;
    case 'track':   return `/prioritization/tracks/${id}`;
    default:        return '';
  }
}

// ============================================================================
// Human-readable summaries
// ============================================================================

function lineageEventSummary(
  eventType: string,
  sourceIds: string[],
  targetIds: string[],
  rationale?: string | null,
): string {
  const n = sourceIds.length;
  const m = targetIds.length;
  switch (eventType) {
    case 'split':     return `Split into ${m} item${m !== 1 ? 's' : ''}`;
    case 'merge':     return `Merged ${n} item${n !== 1 ? 's' : ''} into one`;
    case 'supersede': return rationale ? `Superseded: ${rationale}` : 'Superseded by new version';
    case 'retire':    return rationale ? `Retired: ${rationale}` : 'Retired';
    case 'fork':      return 'Forked as independent copy';
    default:          return eventType;
  }
}

function auditEventSummary(eventType: string): string {
  switch (eventType) {
    case 'item_created':  return 'Item created';
    case 'item_promoted':
    case 'ITEM_PROMOTED': return 'Item promoted from session';
    default:              return eventType.replace(/_/g, ' ');
  }
}

// ============================================================================
// Data loading hook — lineage_events + origin audit_events only
// ============================================================================

const PAGE_SIZE = 200;

interface FeedLoadState {
  events: ProvenanceEvent[];
  loading: boolean;
  error: string | null;
}

function useLineageFeed(refresh: number) {
  const [state, setState] = useState<FeedLoadState>({ events: [], loading: true, error: null });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setState((s) => ({ ...s, loading: true, error: null }));

      const supabase    = getSafeSupabaseClient();
      const workspaceId = getCurrentWorkspaceId();
      const allEvents: ProvenanceEvent[] = [];

      if (supabase && workspaceId) {
        // 1) lineage_events — the primary, canonical lineage source
        //    (split / merge / supersede / retire / fork)
        //    Use .or() to include legacy rows where workspace_id is null
        //    (written before migration 223 added workspace_id).
        const { data: lineageRows, error: lineageErr } = await supabase
          .from('lineage_events')
          .select('id, event_type, source_item_ids, target_item_ids, performed_by, created_at, rationale, metadata')
          .or(`workspace_id.eq.${workspaceId},workspace_id.is.null`)
          .order('created_at', { ascending: false })
          .limit(PAGE_SIZE);

        if (!lineageErr && lineageRows) {
          for (const row of lineageRows as Array<{
            id: string;
            event_type: string;
            source_item_ids: string[];
            target_item_ids: string[];
            performed_by: string | null;
            created_at: string;
            rationale: string | null;
            metadata: Record<string, unknown> | null;
          }>) {
            const primarySourceId = (row.source_item_ids ?? [])[0] ?? null;
            const objectRef: ProvenanceObjectRef | null = primarySourceId
              ? { kind: 'item', id: primarySourceId, href: objectHref('item', primarySourceId) }
              : null;

            allEvents.push({
              id:         row.id,
              kind:       'lineage',
              eventType:  row.event_type,
              summary:    lineageEventSummary(row.event_type, row.source_item_ids ?? [], row.target_item_ids ?? [], row.rationale),
              occurredAt: row.created_at,
              actorId:    row.performed_by ?? null,
              actorName:  null,
              source:     'lineage_events',
              objectRef,
              payload: {
                sourceItemIds: row.source_item_ids,
                targetItemIds: row.target_item_ids,
                rationale:     row.rationale,
                metadata:      row.metadata,
              },
            });
          }
        }

        // 2) audit_events — item origin events only.
        //    'shaping_started' with payload.details.action='item_created' is the
        //    canonical item-creation event; 'item_created' covers newer paths.
        //    Use .or() to include legacy rows where workspace_id is null.
        const { data: auditRows, error: auditErr } = await supabase
          .from('audit_events')
          .select('id, event_type, created_at, created_by, payload, item_id')
          .or(`workspace_id.eq.${workspaceId},workspace_id.is.null`)
          .in('event_type', [...LINEAGE_AUDIT_EVENT_TYPES])
          .order('created_at', { ascending: false })
          .limit(PAGE_SIZE);

        if (!auditErr && auditRows) {
          for (const row of auditRows as Array<{
            id: string;
            event_type: string;
            created_at: string;
            created_by: string | null;
            payload: Record<string, unknown> | null;
            item_id: string | null;
          }>) {
            // For shaping_started, only include item creation events.
            if (row.event_type === 'shaping_started') {
              const action =
                (row.payload?.details as Record<string, unknown> | undefined)?.action ??
                row.payload?.action;
              if (action !== 'item_created') continue;
            }

            const itemId = row.item_id ?? (row.payload?.itemId as string | undefined) ?? null;
            const objectRef: ProvenanceObjectRef | null = itemId
              ? { kind: 'item', id: itemId, href: objectHref('item', itemId) }
              : null;

            // Normalise the display event type to 'item_created' regardless of
            // the underlying DB event_type so the summary and icon are consistent.
            const displayEventType =
              row.event_type === 'shaping_started' ? 'item_created' : row.event_type;

            allEvents.push({
              id:         row.id,
              kind:       'item_transition',
              eventType:  displayEventType,
              summary:    auditEventSummary(displayEventType),
              occurredAt: row.created_at,
              actorId:    row.created_by ?? null,
              actorName:  null,
              source:     'audit_events',
              objectRef,
              payload:    row.payload ?? null,
            });
          }
        }
      }

      const seen = new Set<string>();
      const deduped = allEvents
        .filter((e) => { if (seen.has(e.id)) return false; seen.add(e.id); return true; })
        .sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : a.occurredAt > b.occurredAt ? -1 : 0));

      if (!cancelled) setState({ events: deduped, loading: false, error: null });
    }

    load().catch((err) => {
      if (!cancelled) setState({ events: [], loading: false, error: err instanceof Error ? err.message : 'Failed to load lineage.' });
    });
    return () => { cancelled = true; };
  }, [refresh]);

  return state;
}

// ============================================================================
// Name resolution — converts objectRef IDs to display names using item store
// ============================================================================

function useObjectNameMap(events: ProvenanceEvent[]): Record<string, string> {
  const getItemById = useItemsStore((s) => s.getItemById);

  return useMemo(() => {
    const map: Record<string, string> = {};
    for (const e of events) {
      const ref = e.objectRef;
      if (!ref || ref.kind !== 'item') continue;
      const key = `item:${ref.id}`;
      if (!map[key]) {
        const name = getItemById(ref.id)?.title;
        if (name) map[key] = name;
      }
    }
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);
}

// ============================================================================
// Object pill — linked item badge on each event row
// ============================================================================

function ObjectPill({ ref: objRef, resolvedName }: { ref: ProvenanceObjectRef; resolvedName: string | null }) {
  const label   = PROVENANCE_OBJECT_KIND_LABELS[objRef.kind];
  const rawName = resolvedName ?? objRef.nameHint ?? null;

  const UNNAMED_FALLBACKS: Partial<Record<ProvenanceObjectKind, string>> = {
    item:    'Untitled item',
    session: 'Unnamed session',
    cycle:   'Unnamed cycle',
  };
  const name      = rawName || UNNAMED_FALLBACKS[objRef.kind] || null;
  const displayId = objRef.id.slice(0, 8);

  const inner = (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 border border-[var(--rule)] bg-[var(--bg-raised)] text-xs text-[var(--text-sec)] hover:border-[var(--rule)] hover:bg-[var(--surface)] transition-colors group/pill">
      <span className={`font-medium ${rawName ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)] italic'}`}>
        {name ?? <span className="font-mono text-[var(--text-ter)]">{displayId}…</span>}
      </span>
      <span className="text-[var(--text-ter)]">·</span>
      <span className="text-[var(--text-ter)]">{label}</span>
      {objRef.href && <ExternalLink className="h-3 w-3 text-[var(--text-ter)] group-hover/pill:text-[var(--text-ter)] flex-shrink-0" />}
    </span>
  );

  return objRef.href ? <Link to={objRef.href}>{inner}</Link> : inner;
}

// ============================================================================
// Related items list (for split/merge payloads)
// ============================================================================

function RelatedItemPills({
  ids,
  label,
  nameMap,
}: {
  ids: string[];
  label: string;
  nameMap: Record<string, string>;
}) {
  const [expanded, setExpanded] = useState(false);
  const show = expanded ? ids : ids.slice(0, 3);
  const rest = ids.length - 3;

  return (
    <div className="mt-2">
      <span className="text-xs text-[var(--text-ter)] mr-2">{label}</span>
      <span className="inline-flex flex-wrap gap-1.5 items-center">
        {show.map((id) => {
          const name = nameMap[`item:${id}`];
          return (
            <Link
              key={id}
              to={`/prioritization/items/${id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[var(--rule)] bg-[var(--surface)] text-xs text-[var(--text-sec)] hover:bg-[var(--hover-tint)] hover:border-[var(--rule)] transition-colors"
            >
              {name ?? <span className="font-mono">{id.slice(0, 8)}…</span>}
              <ExternalLink className="h-2.5 w-2.5 text-[var(--text-ter)]" />
            </Link>
          );
        })}
        {!expanded && rest > 0 && (
          <button type="button" onClick={() => setExpanded(true)} className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            +{rest} more
          </button>
        )}
      </span>
    </div>
  );
}

// ============================================================================
// Filter chips
// ============================================================================

function KindChip({ kind, active, count, onClick }: {
  kind: ProvenanceEventKind; active: boolean; count: number; onClick: () => void;
}) {
  const Icon  = KIND_ICONS[kind] ?? GitBranch;
  const color = PROVENANCE_KIND_COLORS[kind];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${
        active
          ? `${color} border-transparent ring-2 ring-offset-1 ring-current/30`
          : 'bg-[var(--bg-raised)] text-[var(--text-ter)] border-[var(--rule)] hover:border-[var(--rule)] hover:text-[var(--text-sec)]'
      }`}
    >
      <Icon className="h-3 w-3" />
      {PROVENANCE_KIND_LABELS[kind]}
      <span className={`ml-0.5 ${active ? 'opacity-70' : 'opacity-40'}`}>{count}</span>
    </button>
  );
}

// ============================================================================
// Event row
// ============================================================================

function EventRow({
  event,
  actorDisplayName,
  objectNameMap,
}: {
  event: ProvenanceEvent;
  actorDisplayName: string | null;
  objectNameMap: Record<string, string>;
}) {
  const [expanded, setExpanded] = useState(false);

  const Icon       = LINEAGE_EVENT_ICONS[event.eventType] ?? KIND_ICONS[event.kind] ?? GitBranch;
  const colorClass = event.kind === 'lineage'
    ? PROVENANCE_KIND_COLORS.lineage
    : PROVENANCE_KIND_COLORS[event.kind] ?? PROVENANCE_KIND_COLORS.other;

  const objRef       = event.objectRef ?? null;
  const resolvedName = objRef ? (objectNameMap[`${objRef.kind}:${objRef.id}`] ?? null) : null;

  const displayActor = actorDisplayName ?? event.actorName;

  // For lineage events, extract related item IDs for display
  const sourceIds  = (event.payload?.sourceItemIds as string[] | undefined) ?? [];
  const targetIds  = (event.payload?.targetItemIds as string[] | undefined) ?? [];
  const hasRelated = (event.eventType === 'split' && targetIds.length > 0) ||
                     (event.eventType === 'merge' && sourceIds.length > 1) ||
                     (event.eventType === 'fork'  && targetIds.length > 0);

  const rationale  = event.payload?.rationale as string | undefined;

  return (
    <div className="px-4 py-3 hover:bg-[var(--surface)]/60 transition-colors">
      <div className="flex items-start gap-3">
        {/* Kind icon */}
        <div className={`mt-0.5 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${colorClass}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Header: kind badge + actor + time */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
              {PROVENANCE_KIND_LABELS[event.kind]}
            </span>
            <div className="flex items-center gap-3 text-xs text-[var(--text-ter)] flex-shrink-0">
              {displayActor && (
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />{displayActor}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />{relativeTime(event.occurredAt)}
              </span>
            </div>
          </div>

          {/* Summary */}
          <p className="text-sm text-[var(--text-pri)] mt-1 leading-snug">{event.summary}</p>

          {/* Rationale */}
          {rationale && (
            <p className="text-xs text-[var(--text-ter)] italic mt-0.5 border-l-2 border-[var(--rule)] pl-2">
              "{rationale}"
            </p>
          )}

          {/* Object pill + journey link — the primary item involved */}
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {objRef && <ObjectPill ref={objRef} resolvedName={resolvedName} />}
            {objRef?.kind === 'item' && (
              <Link
                to={`/prioritization/items/${objRef.id}/journey`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs font-medium hover:bg-[var(--green-dim)] hover:border-[var(--green)] transition-colors"
              >
                <MapIcon className="h-3 w-3" />
                Journey
              </Link>
            )}
          </div>

          {/* Related items for split/merge/fork */}
          {hasRelated && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="mt-1.5 inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
            >
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {expanded ? 'Hide related items' : 'Show related items'}
            </button>
          )}
          {expanded && event.eventType === 'split' && targetIds.length > 0 && (
            <RelatedItemPills ids={targetIds} label="Created:" nameMap={objectNameMap} />
          )}
          {expanded && event.eventType === 'merge' && sourceIds.length > 1 && (
            <RelatedItemPills ids={sourceIds} label="Sources:" nameMap={objectNameMap} />
          )}
          {expanded && event.eventType === 'fork' && targetIds.length > 0 && (
            <RelatedItemPills ids={targetIds} label="Fork:" nameMap={objectNameMap} />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function relativeTime(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 86400 * 7) return `${Math.floor(seconds / 86400)}d ago`;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(iso));
}

function dateGroup(iso: string): string {
  const date      = new Date(iso);
  const today     = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString())     return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

// ============================================================================
// Main page
// ============================================================================

export default function LineagePage() {
  const [refresh, setRefresh] = useState(0);
  const { events, loading, error } = useLineageFeed(refresh);

  const [search,      setSearch]      = useState('');
  const [activeKinds, setActiveKinds] = useState<Set<ProvenanceEventKind>>(new Set());
  const [showKindBar, setShowKindBar] = useState(true);

  const actorIds = useMemo(
    () => [...new Set(events.map((e) => e.actorId).filter((id): id is string => !!id))],
    [events],
  );
  const actorDisplayMap = useActorDisplayMap(actorIds);
  const objectNameMap   = useObjectNameMap(events);

  const toggleKind   = useCallback((k: ProvenanceEventKind) => {
    setActiveKinds((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }, []);
  const clearFilters = () => setActiveKinds(new Set());

  const filtered = useMemo(() => {
    let items = events;
    if (activeKinds.size > 0) items = items.filter((e) => activeKinds.has(e.kind));
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter((e) => {
        const actor      = actorDisplayMap[e.actorId ?? ''] ?? e.actorName ?? '';
        const objKey     = e.objectRef ? `${e.objectRef.kind}:${e.objectRef.id}` : '';
        const objectName = objKey ? (objectNameMap[objKey] ?? e.objectRef?.nameHint ?? '') : '';
        return (
          e.summary.toLowerCase().includes(q) ||
          e.eventType.toLowerCase().includes(q) ||
          actor.toLowerCase().includes(q) ||
          objectName.toLowerCase().includes(q)
        );
      });
    }
    return items;
  }, [events, activeKinds, search, actorDisplayMap, objectNameMap]);

  const groups = useMemo(() => {
    const map = new Map<string, ProvenanceEvent[]>();
    for (const ev of filtered) {
      const label = dateGroup(ev.occurredAt);
      const arr   = map.get(label) ?? [];
      arr.push(ev);
      map.set(label, arr);
    }
    return map;
  }, [filtered]);

  const kindCounts = useMemo(() => {
    const counts: Partial<Record<ProvenanceEventKind, number>> = {};
    for (const e of events) counts[e.kind] = (counts[e.kind] ?? 0) + 1;
    return counts;
  }, [events]);

  // Only show filter chips for kinds that have events
  const presentKinds = LINEAGE_KINDS.filter((k) => (kindCounts[k] ?? 0) > 0);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={<GitBranch className="h-4 w-4 text-[var(--green)]" />}
        iconBg="bg-[var(--green-dim)]"
        title="Lineage"
        subtitle="Where items came from — splits, merges, supersessions, retirements, forks, and promotions"
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/prioritization/workspace-journey"
              className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] text-sm font-medium hover:bg-[var(--slate-dim)] hover:border-[var(--slate)] transition-colors"
            >
              <MapIcon className="h-4 w-4" />
              Workspace Journey
            </Link>
            <button
              type="button"
              onClick={() => setRefresh((r) => r + 1)}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] text-sm font-medium hover:bg-[var(--surface)] transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        }
      />

      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        {/* Search + filter */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--rule)]">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by summary, actor, or item name…"
              className="w-full pl-9 pr-4 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-[var(--green)]"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {presentKinds.length > 1 && (
            <button
              type="button"
              onClick={() => setShowKindBar((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-2  text-sm font-medium border transition-colors ${
                activeKinds.size > 0
                  ? 'bg-[var(--green)] text-white border-[var(--green)]'
                  : 'bg-[var(--bg-raised)] text-[var(--text-sec)] border-[var(--rule)] hover:bg-[var(--surface)]'
              }`}
            >
              <Filter className="h-3.5 w-3.5" />
              {activeKinds.size > 0 ? `${activeKinds.size} filter${activeKinds.size > 1 ? 's' : ''}` : 'Filter'}
            </button>
          )}
          {activeKinds.size > 0 && (
            <button type="button" onClick={clearFilters} className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors">
              Clear
            </button>
          )}
        </div>

        {/* Kind chips */}
        {showKindBar && presentKinds.length > 1 && (
          <div className="flex flex-wrap gap-2 px-4 py-3 bg-[var(--surface)]/50 border-b border-[var(--rule)]">
            {presentKinds.map((k) => (
              <KindChip
                key={k}
                kind={k}
                active={activeKinds.has(k)}
                count={kindCounts[k] ?? 0}
                onClick={() => toggleKind(k)}
              />
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 px-4 py-2 text-xs text-[var(--text-ter)] border-b border-[var(--rule)] bg-[var(--bg-raised)]">
          <span>{events.length} lineage event{events.length !== 1 ? 's' : ''}</span>
          {(activeKinds.size > 0 || search) && (
            <span className="text-[var(--green)] dark:text-[var(--text-ter)] font-medium">{filtered.length} matching</span>
          )}
        </div>

        {/* Feed */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin h-6 w-6 border-2 border-[var(--green)] border-t-transparent rounded-full" />
          </div>
        ) : error ? (
          <div className="p-8 text-center text-[var(--coral)] text-sm">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-8">
            <GitBranch className="h-10 w-10 text-[var(--text-ter)] mb-3" />
            <p className="text-[var(--text-ter)] font-medium">
              {events.length === 0 ? 'No lineage events recorded yet' : 'No events match your filters'}
            </p>
            {events.length === 0 ? (
              <div className="mt-3 space-y-2 max-w-md">
                <p className="text-[var(--text-ter)] text-sm">
                  Lineage events are recorded when items are <strong>split</strong>, <strong>merged</strong>, <strong>superseded</strong>, <strong>retired</strong>, or <strong>forked</strong>. Item creation events also appear here.
                </p>
                <p className="text-[var(--text-ter)] text-sm">
                  To perform a lineage action, open an item and use the <strong>Lineage</strong> tab, then choose Split, Merge, Supersede, or Retire.
                </p>
                <p className="text-[var(--text-ter)] text-sm">
                  For a graphical view of an individual item's full lifecycle, open the item and click <strong>View journey</strong>.
                </p>
              </div>
            ) : null}
            {activeKinds.size > 0 && (
              <button type="button" onClick={clearFilters} className="mt-3 text-sm text-[var(--green)] dark:text-[var(--text-ter)] hover:underline">
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-[var(--rule)]">
            {Array.from(groups.entries()).map(([label, groupEvents]) => (
              <div key={label}>
                <div className="px-4 py-2 bg-[var(--surface)] text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide sticky top-0 z-10 border-b border-[var(--rule)]">
                  {label}
                </div>
                {groupEvents.map((ev) => (
                  <EventRow
                    key={ev.id}
                    event={ev}
                    actorDisplayName={
                      ev.actorId && actorDisplayMap[ev.actorId]
                        ? actorDisplayMap[ev.actorId]
                        : ev.actorName ?? null
                    }
                    objectNameMap={objectNameMap}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
